// ============================================================================
// 부트스트랩 + 루프. 물리 240Hz 고정 간격, 렌더는 보간.
// 화면은 둘: open(개봉) / binder(바인더). 한 스텝에 활성 sim 하나만 돈다.
// 키: R 새 팩(공짜 — 값은 뜯을 때 낸다) / B 바인더 / H 패널 / D 진단 / Space A/B  (버튼을 감춰도 단축키는 그대로 동작한다)
// URL: ?haptic=0  ?asset=0  ?p=<설정>  ?panel=1  ?seed=<정수>  ?binder=1  ?face=<n>
// ============================================================================
import { P, WORLD, DEFAULT_PARAMS, assignParams, setDrawSeed, CARD_DRAW, BINDER_LAYERS } from './params';
import { PointerInput } from './input';
import { Sim, RS } from './sim';
import { Renderer } from './render';
import { Diag } from './diag';
import { Panel, unb64url } from './panel';
import { audio } from './audio';
import { bgm } from './bgm';
import { haptics } from './haptics';
import { makeFallbackSet, loadRealAssets } from './assets';
import { BinderSim, BinderRS } from './binder-sim';
import { CARD_BY_ID, CARD_DEFS, CardDef, cardBySlot, faceCount, overrideRegistry } from './cards.ts';
import { SAVE_KEY, addCards, clearBinder, emptySave, loadBinder, parseSave, saveBinder, totalOwned } from './save.ts';
import {
  adRewardCents,
  buyPack,
  canAffordPack,
  consumePack,
  discardPack,
  fmtCents,
  grantAd,
  isDeadEnd,
  packPriceCents,
  sellOne,
  sellPriceCents,
  sellableCount,
  sellableValue,
} from './econ.ts';
import { isValidMeta, setBinderMeta } from './binder-layout';

const url = new URL(location.href);
if (url.searchParams.get('haptic') === '0') haptics.enabled = false;
const useAssets = url.searchParams.get('asset') !== '0';
// 기본 화면에는 리셋·바인더 버튼만 둔다. 튜닝 패널과 ⚙/D/A-B 버튼은 ?panel=1 일 때만.
const devUI = url.searchParams.get('panel') === '1';
const pParam = url.searchParams.get('p');
if (pParam) {
  try {
    assignParams(P, JSON.parse(unb64url(pParam)));
  } catch (e) {
    console.warn('URL 설정 파싱 실패', e);
  }
}
// 뽑기 시드. 있으면 리셋해도 같은 8장 — 검증 도구와 A/B 비교가 여기에 의존한다.
const seedParam = url.searchParams.get('seed');
setDrawSeed(seedParam !== null && seedParam.trim() !== '' && Number.isFinite(Number(seedParam)) ? Number(seedParam) | 0 : null);
void DEFAULT_PARAMS;

const canvas = document.getElementById('gl') as HTMLCanvasElement;
const overlay = document.getElementById('overlay') as HTMLCanvasElement;
const pageEl = document.getElementById('page')!;
const detailEl = document.getElementById('detail-info')!;
const walletEl = document.getElementById('wallet')!;
const shopMsgEl = document.getElementById('shop-msg')!;
const sellBtn = document.getElementById('sell') as HTMLButtonElement;
const adBtn = document.getElementById('btn-ad') as HTMLButtonElement;
const adLongBtn = document.getElementById('btn-ad-long') as HTMLButtonElement;

// 폴백 텍스처로 즉시 시작 (로딩 화면 없음)
const tex = makeFallbackSet(P.cut.perfFrac);
let renderer!: Renderer;
const input = new PointerInput(canvas, (x, y) => renderer.toWorld(x, y));
const sim = new Sim(input);
renderer = new Renderer(canvas, sim, tex);
sim.aspect = renderer.aspect;
const diag = new Diag(sim, input, renderer, overlay);

// ---------------------------------------------------------------- 저장소 / 바인더
const save = loadBinder();
const binderSim = new BinderSim(input);
binderSim.setSave(save);
binderSim.aspect = renderer.aspect;
// 창 크기가 바뀌면 즉시 반영한다 — 펼침(와이드) 모드 판정이 aspect 하나에서 나온다.
renderer.onResize = (a) => (binderSim.aspect = a);
binderSim.onPrefetch = (slot) => renderer.binder.prefetchDetail(slot);
renderer.binder.setCounts(save.cards);

type Screen = 'title' | 'open' | 'binder';
// **타이틀에서 시작한다.** 검증 도구는 ?title=0 으로 건너뛴다 (아래 부트 참고).
let screen: Screen = 'title';
let autoEntered = false; // 8장을 다 넘겨 자동으로 들어왔는가 (나갈 때 새 팩)

// 바인더 이벤트도 진단에 흘려 보낸다 (도구가 __cr.events() 로 읽는다)
const eventLog: string[] = [];
function logEvent(name: string): void {
  eventLog.push(name);
  if (eventLog.length > 64) eventLog.shift();
}
binderSim.onEvent = (n) => {
  logEvent(n);
  diag.noteEvent(n);
  if (n === 'detail-closed') renderer.binder.evictDetail();
};
renderer.binder.onDetailTex = () => {
  logEvent('detail-tex');
  diag.noteEvent('detail-tex');
};
const simEvent = (n: string) => {
  logEvent(n);
  diag.noteEvent(n);
};

const panel = new Panel({
  reset: () => reset(),
  toggleDiag: (force) => {
    diag.toggle(force);
    panel.setDiagChecked(diag.visible);
  },
  setGraphics: (on) => (renderer.graphicsOn = on),
  onParamsApplied: () => {
    // 절취선/지글거림 값이 바뀌면 다음 리셋부터 반영. 아직 안 잘랐으면 즉시 반영.
    if (sim.phase === 'pack') renderer.rebuildPack();
  },
  binderSave: () => binderSim.save,
  binderApply: (json) => applyBinderJson(json),
  binderClear: () => {
    clearBinder();
    const s = emptySave();
    binderSim.setSave(s);
    renderer.binder.setCounts(s.cards);
  },
  binderFill: () => {
    const s = binderSim.save;
    for (const d of CARD_DEFS) if (Math.random() < 0.6) s.cards[d.id] = 1 + ((Math.random() * 9) | 0);
    saveBinder(s);
    renderer.binder.invalidate();
  },
});
document.body.classList.toggle('devui', devUI);
panel.toggle(devUI);

function applyBinderJson(json: string | object): void {
  const text = typeof json === 'string' ? json : JSON.stringify(json);
  const r = parseSave(text);
  binderSim.setSave(r.save);
  renderer.binder.setCounts(r.save.cards);
  saveBinder(r.save);
}

if (useAssets) {
  loadRealAssets(
    (name, t) => renderer.setTexture(name, t),
    (meta) => {
      if (meta.perfFrac !== undefined && !pParam) {
        P.cut.perfFrac = meta.perfFrac;
        panel.refresh();
        if (sim.phase === 'pack') renderer.rebuildPack();
      }
      // 측정 좌표가 도착하면 그림도 좌표도 같이 갈아끼운다 (폴백 좌표로 배치한 칸을 다시 배치)
      if (meta.binder && isValidMeta(meta.binder)) {
        setBinderMeta(meta.binder);
        renderer.binder.setLayout();
      }
    },
  );
}

function reset(): void {
  sim.reset();
  renderer.rebuildPack();
}

// ---------------------------------------------------------------- 경제 (결제 / 판매 / 광고)
// **결제는 "뜯는 순간"이다.** 팩이 화면에 놓여 있기만 하면 한 푼도 안 나가고, 리셋해서 새 팩을
// 봐도 공짜다. 값은 손가락이 절취선을 실제로 긋기 시작할 때(sim 의 cut-start 지점) 빠진다.
// save.openPaid 가 "값을 치른 = 이미 뜯기 시작한 팩" 이라는 뜻이고 localStorage 에 남는다 —
// 안 그러면 자르다 새로고침할 때마다 팩 값을 다시 내게 된다.
//
// 돈이 모자라면 팩은 **잠긴다**(입력 무시 + 상점 자동 표시). 팩을 아예 안 그리는 대신
// 잠그는 쪽을 골랐다: 렌더러를 건드리지 않고, "살 물건이 눈앞에 있다"가 더 읽히기 때문이다.
let adLongUntil = 0; // 긴 광고 재생 종료 시각 (performance.now 기준). 0 = 재생 중 아님
let shopOpen = false;
let wasLocked = false; // 직전 프레임의 packLocked() — 상점을 잠길 때 한 번만 열기 위해

/** 이 팩을 뜯을 수 없다 = 아직 값을 안 치렀는데 돈도 모자란다. */
function packLocked(): boolean {
  const s = binderSim.save;
  return !s.openPaid && !canAffordPack(s);
}

/**
 * **자르기 결제.** sim 이 "팩이 찢어지기 시작하는 첫 프레임" 에 딱 한 번 부른다 (Sim.onCutCharge).
 * 이미 값을 치른 팩이면 아무 일도 안 한다 — **한 팩에 한 번만** 빠진다.
 * 돈이 모자라면 false → 자르기가 시작되지 않고 상점이 뜬다.
 */
function chargeForCut(): boolean {
  const s = binderSim.save;
  if (s.openPaid) return true;
  if (!buyPack(s)) {
    shopOpen = true;
    logEvent('buy-failed');
    return false;
  }
  // 소리는 판매와 같은 마른 클릭을 재사용한다. **진동은 안 준다** — 바로 뒤에 관통 펄스와
  // 연속 진동이 이어지는데 navigator.vibrate 는 새 호출이 이전 패턴을 취소해서 자르기 손맛을 깎는다.
  audio.clickRelease(1);
  sim.showFloat(`-${fmtCents(packPriceCents())}`, -1);
  logEvent('pay');
  return true;
}

/**
 * "새 팩" 경로 (R / 리셋 버튼 / 자동 진입 뒤 닫기). **리셋은 언제나 공짜다.**
 *
 * 안 건드린 팩이면 값을 치른 적이 없으니 그냥 다시 뽑는 것이고,
 * **자르기 시작한 뒤 리셋하면 그 팩은 사라진다 (환불 없음).** 환불해 주면 넘기다가
 * 커먼만 나올 때 리셋해서 무한 재추첨이 되고, 팩당 -$0.79 싱크가 통째로 무너진다.
 * 새 팩은 다시 뜯을 때 값을 치른다.
 */
function newPack(): void {
  discardPack(binderSim.save); // 뜯다 만 팩은 버려진다 (환불 없음)
  reset();
  audio.thud(1); // 팩이 테이블에 떨어지는 소리 — 기존 착지음 재사용
  haptics.pulse(P.haptic.land);
}

/**
 * 판매 금액을 바인더 화면에 띄운다. 개봉 화면의 떠오르는 텍스트는 three(`sim.float`)이지만
 * 바인더에는 그 경로가 없어서 HTML 오버레이로 한다. **CSS transition 은 안 쓴다** —
 * 위치·투명도를 매 프레임 계산해서 넣는다 (절대 규칙).
 */
const gainEl = document.getElementById('gain') as HTMLDivElement;
let gainT = -1;
function showGain(text: string): void {
  gainEl.textContent = text;
  gainT = 0;
}
function stepGain(dt: number): void {
  if (gainT < 0) return;
  gainT += dt;
  const dur = Math.max(0.05, P.econ.floatDur);
  if (gainT >= dur) {
    gainT = -1;
    gainEl.style.opacity = '0';
    return;
  }
  const k = gainT / dur;
  gainEl.style.opacity = String(1 - k * k); // 끝에서 빨리 사라진다
  // 지갑(우상단, top=max(8px,safe) + 높이 30px) 바로 아래에서 시작해 위로 떠오른다.
  // 위쪽에 두는 이유: 아래쪽은 판매 버튼을 누르는 손가락에 가린다.
  gainEl.style.top = `calc(max(8px, env(safe-area-inset-top)) + ${42 - k * P.econ.floatRise * 6}px)`;
}

function doSell(): void {
  const def = cardBySlot(binderSim.det.slot);
  if (!def) return;
  const s = binderSim.save;
  const got = sellOne(s, def.id);
  if (got === null) return;
  renderer.binder.setCounts(s.cards);
  showGain(`+${fmtCents(got)}`); // 판매는 **획득**이다 — 팩 구매의 -$N 과 부호·색이 반대여야 한다
  audio.clickRelease(1); // 짧고 마른 클릭. 여러 번 누르는 동작이라 큰 소리를 붙이면 피곤하다
  haptics.pulse(P.haptic.slip);
  logEvent('sell');
}

function doAd(long: boolean): void {
  if (adLongUntil > 0) return;
  if (long && P.econ.adLongWait > 0) {
    // 긴 광고는 **대기 시간을 둔다.** 즉시 지급하면 짧은 광고와 구분이 없고,
    // 나중에 진짜 SDK 가 들어올 자리(재생 시간)를 지금부터 비워 두는 편이 낫다.
    adLongUntil = performance.now() + P.econ.adLongWait * 1000;
    logEvent('ad-start');
    return;
  }
  finishAd(long);
}

function finishAd(long: boolean): void {
  const s = binderSim.save;
  grantAd(s, long);
  audio.complete(); // 보상 = 완료음 재사용 (밝은 원샷)
  haptics.pulse([14, 60, 14]);
  logEvent('ad');
}

// ---------------------------------------------------------------- 타이틀 화면
//
// 키아트 한 장 + TAP TO START 버튼. 물리는 안 돈다 (개봉 sim 은 시간이 멈춘 채 대기).
// **움직임은 전부 여기서 매 프레임 계산한다** — CSS transition/animation 금지(절대 규칙).
//   · 숨쉬기: 크기를 사인파로 ±pulseAmp
//   · 눌림: 아래로 btnPressDrop px, 밝기 btnPressDim
//   · 페이드아웃: 누른 뒤 fadeDur 초에 걸쳐 화면 전체 불투명도를 1 → 0
const titleEl = document.getElementById('title')!;
const tapBtn = document.getElementById('tap-start')!;
const titleArt = document.getElementById('title-art') as HTMLImageElement;
let titleT = 0; // 타이틀에 머문 시간 (s) — 숨쉬기 위상
let titlePressed = false; // 손가락이 버튼 위에 눌려 있는가
let titleFade = -1; // 페이드 진행 (s). 음수 = 아직 시작 안 함

function startGame(): void {
  if (titleFade >= 0) return; // 연타 방지 — 이미 걷히는 중이면 무시
  audio.warmup(); // 사용자 제스처 안에서 오디오 컨텍스트를 깨운다 (자동재생 정책)
  haptics.interacted = true;
  bgm.stop(P.bgm.fadeOut); // 화면이 걷히는 동안 BGM 도 같이 걷힌다
  titleFade = 0;
}

/**
 * 타이틀 BGM 켜기. **자동재생 정책 때문에 사용자 제스처 없이는 소리가 안 난다** —
 * 페이지를 열자마자 부르면 컨텍스트가 suspended 로 남는다. 그래서
 *   · 부팅 때 한 번 시도하고 (이미 상호작용한 뒤의 새로고침이면 바로 난다)
 *   · 안 났으면 **화면 어디든 첫 입력**에서 다시 시도한다 (START 를 누르기 전에 울려야 하므로
 *     버튼이 아니라 window 에 건다).
 * 헤드리스 자동화(`navigator.webdriver`)에서는 아예 안 켠다 — 검증 도구가 개봉 화면을 전제한다.
 */
function ensureTitleBgm(): boolean {
  if (screen !== 'title' || titleFade >= 0 || !P.bgm.on) return false;
  if (navigator.webdriver === true && new URL(location.href).searchParams.get('bgm') !== '1') return false;
  audio.warmup();
  if (!audio.ctx || audio.ctx.state !== 'running') return false;
  bgm.start();
  return true;
}

/** 타이틀 화면의 매 프레임 계산. dt 는 초. */
function stepTitle(dt: number): void {
  const T = P.title;
  titleT += dt;

  // 페이드가 끝나면 개봉 화면으로 넘긴다
  if (titleFade >= 0) {
    titleFade += dt;
    const a = T.fadeDur > 0 ? Math.min(1, titleFade / T.fadeDur) : 1;
    titleEl.style.opacity = String(1 - a);
    if (a >= 1) {
      screen = 'open';
      titleFade = -1;
      titleEl.style.opacity = '1';
      syncOverlay();
      logEvent('title:start');
    }
    return;
  }

  // 버튼 위치 — **그려진 그림의 빈 띠** 안에 있어야 한다. 화면 높이로 잡으면
  // contain 인 가로 화면에서 그림이 화면보다 작아 띠 밖으로 나간다 (2026-09-19).
  const h = window.innerHeight;
  const artW = titleArt.naturalWidth || 1080;
  const artH = titleArt.naturalHeight || 1920;
  const contain = getComputedStyle(titleArt).objectFit === 'contain';
  // contain 이면 짧은 쪽에 맞춰 축소되고, cover 면 화면을 꽉 채운다
  const sc = contain
    ? Math.min(window.innerWidth / artW, h / artH)
    : Math.max(window.innerWidth / artW, h / artH);
  const drawH = artH * sc;
  // 그림 아래 가장자리의 화면 y. contain 이면 위아래 같은 여백, cover 는 bottom 정렬이라 화면 바닥.
  const artBottom = contain ? h / 2 + drawH / 2 : h;
  const drop = titlePressed ? T.btnPressDrop : 0;
  tapBtn.style.bottom = `${h - artBottom + drawH * T.btnBottomFrac - drop}px`;

  // 숨쉬기 — 누르고 있는 동안은 멈춘다 (손가락 아래서 크기가 변하면 어색하다)
  const pulse = !titlePressed && T.pulsePeriod > 0 ? 1 + T.pulseAmp * Math.sin((titleT / T.pulsePeriod) * Math.PI * 2) : 1;
  tapBtn.style.transform = `translateX(-50%) scale(${pulse.toFixed(4)})`;
  tapBtn.style.filter = titlePressed ? `brightness(${T.btnPressDim})` : 'none';
  // 눌리면 아래 그림자도 같이 줄어야 눌린 것처럼 보인다
  tapBtn.style.boxShadow = titlePressed
    ? '0 2px 0 #1F8C90, 0 4px 12px rgba(64,216,219,.3)'
    : '0 6px 0 #1F8C90, 0 10px 26px rgba(64,216,219,.42)';
}

// pointerdown/up 을 직접 받는다 (input.ts 는 게임 제스처용이라 타이틀에는 안 쓴다)
tapBtn.addEventListener('pointerdown', (e) => {
  e.preventDefault();
  titlePressed = true;
});
// **첫 입력에서 BGM 을 깨운다.** START 버튼이 아니라 window 에 거는 이유는
// 버튼을 누르면 곧바로 화면이 걷혀서 BGM 을 들려줄 기회가 없기 때문이다.
// 성공하면 리스너를 떼어 이후 게임 입력에 얹히지 않게 한다.
function bgmGestureHook(): void {
  if (ensureTitleBgm() || screen !== 'title') {
    window.removeEventListener('pointerdown', bgmGestureHook, true);
    window.removeEventListener('keydown', bgmGestureHook, true);
  }
}
window.addEventListener('pointerdown', bgmGestureHook, true);
window.addEventListener('keydown', bgmGestureHook, true);
// 버튼 밖에서 떼도 눌림이 안 풀리면 눌린 채로 굳는다
window.addEventListener('pointerup', () => (titlePressed = false));
window.addEventListener('pointercancel', () => (titlePressed = false));
tapBtn.addEventListener('click', startGame);

// ---------------------------------------------------------------- 화면 전환
function openBinder(face?: number): void {
  if (screen === 'binder') return;
  sim.clearPending();
  sim.clearFloat(); // 개봉 화면의 금액 텍스트를 끈다 — 안 끄면 바인더에서 -$4.49 가 그대로 떠 있다
  binderSim.clearPending();
  audio.silenceContinuous();
  haptics.stop();
  binderSim.aspect = renderer.aspect;
  binderSim.enter({ face: face ?? binderSim.save.lastFace });
  screen = 'binder';
  syncOverlay();
}

function closeBinder(): void {
  if (screen !== 'binder') return;
  binderSim.clearPending();
  sim.clearPending();
  audio.silenceContinuous();
  screen = 'open';
  if (autoEntered) newPack(); // 자동 진입 뒤 닫으면 **새 팩을 산다** (§2.5 + 경제)
  autoEntered = false;
  syncOverlay();
}

function toggleBinder(): void {
  if (screen === 'binder') binderSim.requestExit();
  else openBinder();
}

/** 한 팩을 다 넘긴 순간 한 번에 커밋한다. 넘기다 리셋하면 그 팩은 통째로 버려진다 (§2.6). */
function commitPack(): number {
  const s = binderSim.save;
  const n = addCards(s, CARD_DRAW.defIds);
  // 등급에 카드가 한 장도 없으면 defIds 가 null 이라 **조용히 버려진다.** 눈으로 세다 발견한 적이 있어
  // 흔적을 남긴다 (레지스트리에 UR 이 0장이면 2% 가 통째로 사라진다).
  if (n < CARD_DRAW.defIds.length) {
    const lost: string[] = [];
    for (let i = 0; i < CARD_DRAW.defIds.length; i++) {
      const k = CARD_DRAW.kinds[i].replace('card_', '');
      if (!CARD_DRAW.defIds[i] && !lost.includes(k)) lost.push(k);
    }
    console.warn(
      `[commit] 뽑은 ${CARD_DRAW.defIds.length}장 중 ${n}장만 저장됐다 — 등급 ${lost.join(',')} 에 레지스트리 카드가 없다 ` +
        `(tools/build-registry.mjs 로 그 등급 카드를 채워라)`,
    );
  }
  consumePack(s); // 이 팩은 다 썼다 — 다음 팩은 다시 사야 한다
  saveBinder(s);
  renderer.binder.setCounts(s.cards);
  logEvent('commit');
  return n;
}

function checkTransitions(): void {
  if (screen === 'open') {
    // done 이벤트가 아니라 **상태**를 본다 — 날아가는 카드가 화면에서 다 빠진 뒤여야 한다.
    if (sim.phase === 'done' && sim.sw.flyers.length === 0 && !autoEntered) {
      commitPack();
      sim.clearPending();
      binderSim.clearPending();
      audio.silenceContinuous();
      binderSim.aspect = renderer.aspect;
      // 시작 면은 넘기지 않는다 — beginStow() 가 슬롯 순서로 정렬한 뒤 **첫 카드의 면**으로 맞춘다.
      binderSim.enter({ stow: CARD_DRAW.defIds });
      screen = 'binder';
      autoEntered = true;
      syncOverlay();
    }
  } else if (binderSim.exitRequested) {
    binderSim.exitRequested = false;
    closeBinder();
  }
}

// ---------------------------------------------------------------- HTML 오버레이
let lastPageText = '';
let lastDetailText = '';
function syncOverlay(): void {
  document.body.classList.toggle('title', screen === 'title');
  document.body.classList.toggle('binder', screen === 'binder');
  const btn = document.getElementById('btn-binder')!;
  btn.textContent = screen === 'binder' ? '✕' : 'B';
  btn.setAttribute('title', screen === 'binder' ? '닫기 (B)' : '바인더 (B)');
  lastPageText = '';
}

/**
 * 지갑 · 상점 · 판매 버튼. **화면 둘 다에서 매 프레임 부른다.**
 * 텍스트는 바뀔 때만 갈아끼운다 (매 프레임 textContent 를 쓰면 레이아웃이 계속 다시 계산된다).
 */
let lastWalletText = '';
let lastShopMsg = '';
let lastAdLongText = '';
let lastSellText = '';
let lastSellShown = true;
function updateEcon(): void {
  const s = binderSim.save;

  // 긴 광고 대기 — CSS animation 이 아니라 프레임마다 남은 시간을 다시 쓴다
  if (adLongUntil > 0 && performance.now() >= adLongUntil) {
    adLongUntil = 0;
    finishAd(true);
  }

  const money = fmtCents(s.money);
  if (money !== lastWalletText) {
    lastWalletText = money;
    walletEl.textContent = money;
  }
  const broke = !s.openPaid && !canAffordPack(s);
  walletEl.classList.toggle('broke', broke);

  // 개봉 화면에서 팩이 잠기면 상점을 **한 번** 자동으로 연다 — 막다른 길을 눈에 안 보이게 두지 않는다.
  // 바인더에서는 자동으로 안 연다 (카드를 팔러 들어온 것이라 시야를 막으면 안 된다).
  //
  // **매 프레임 열지 않는다.** 예전엔 packLocked() 동안 계속 shopOpen = true 였다.
  // 그래서 닫기를 눌러도 다음 프레임에 다시 떠서 광고를 봐야만 빠져나갈 수 있었다.
  // 카드 판매라는 회복 경로가 있으므로 가둘 이유가 없다 (2026-09-11 사용자 요청).
  const locked = screen === 'open' && packLocked();
  if (locked && !wasLocked) shopOpen = true;
  wasLocked = locked;
  document.body.classList.toggle('shop', shopOpen);

  if (shopOpen) {
    const price = packPriceCents();
    const playing = adLongUntil > 0;
    adBtn.textContent = `광고 보기  +${fmtCents(adRewardCents(false))}`;
    const adLongText = playing
      ? `광고 재생 중…  ${((adLongUntil - performance.now()) / 1000).toFixed(1)}s`
      : `긴 광고 보기  +${fmtCents(adRewardCents(true))}`;
    if (adLongText !== lastAdLongText) {
      lastAdLongText = adLongText;
      adLongBtn.textContent = adLongText;
    }
    adBtn.disabled = playing;
    adLongBtn.disabled = playing;

    let msg: string;
    let warn = false;
    if (s.openPaid) {
      msg = `이 팩은 값을 치렀다. 잔액 ${money}.`;
    } else if (canAffordPack(s)) {
      // **팩을 뜯을 때 빠진다** 는 것을 여기서 말해 준다 — 이제 화면의 팩은 공짜로 놓여 있다.
      msg = `팩을 뜯으면 ${fmtCents(price)} 가 빠진다. 남는 돈 ${fmtCents(s.money - price)}.`;
    } else if (isDeadEnd(s)) {
      warn = true;
      msg = `팩 값 ${fmtCents(price)} 이 모자라고, 팔 수 있는 중복 카드도 없다. 광고로만 회복할 수 있다.`;
    } else {
      warn = true;
      msg = `팩 값 ${fmtCents(price)} 이 모자란다. 바인더(B)에서 중복 카드를 팔 수 있다 (최대 ${fmtCents(sellableValue(s))}).`;
    }
    if (msg !== lastShopMsg) {
      lastShopMsg = msg;
      shopMsgEl.textContent = msg;
    }
    shopMsgEl.classList.toggle('warn', warn);
  }

  // ---- 판매 버튼: 상세 보기에서만. 미보유면 아예 숨기고, 마지막 1장이면 비활성 + 이유를 적는다
  const detailShown = screen === 'binder' && binderSim.mode === 'detail' && binderSim.det.phase === 'shown';
  const def = detailShown ? cardBySlot(binderSim.det.slot) : undefined;
  const owned = def ? s.cards[def.id] ?? 0 : 0;
  const show = !!def && owned > 0;
  if (show !== lastSellShown) {
    lastSellShown = show;
    sellBtn.style.display = show ? '' : 'none';
  }
  if (show && def) {
    const dup = sellableCount(s, def.id);
    const text = dup > 0 ? `팔기  +${fmtCents(sellPriceCents(def.rank))}  (중복 ${dup})` : '마지막 1장 — 팔 수 없다';
    if (text !== lastSellText) {
      lastSellText = text;
      sellBtn.textContent = text;
    }
    sellBtn.disabled = dup <= 0;
  }
}

function updateOverlay(): void {
  updateEcon();
  if (screen !== 'binder') {
    if (document.body.classList.contains('detail')) document.body.classList.remove('detail');
    return;
  }
  // 면 번호는 **정착할 때만** 갱신한다 (넘어가는 중에는 이전 번호 유지)
  if (binderSim.mode === 'idle' || binderSim.mode === 'detail' || binderSim.mode === 'stow') {
    const t = pageText();
    if (t !== lastPageText) {
      lastPageText = t;
      pageEl.textContent = t;
    }
  }
  // 상세 정보는 정착 시점에만 (열리는 중·이웃으로 넘기는 중에는 숨김)
  const shown = binderSim.mode === 'detail' && binderSim.det.phase === 'shown';
  document.body.classList.toggle('detail', shown);
  if (shown) {
    const def = cardBySlot(binderSim.det.slot);
    const t = def ? detailLine(def) : '';
    if (t !== lastDetailText) {
      lastDetailText = t;
      detailEl.textContent = t;
    }
  }
}

/** 면 번호. 펼침에서는 좌우 두 면을 같이 보여준다 (· = 커버 속면). */
function pageText(): string {
  const F = faceCount();
  if (!binderSim.spread) return `${binderSim.face} / ${F}`;
  const l = binderSim.leftFace, r = binderSim.rightFace;
  return `${l >= 1 ? l : '·'}-${r >= 1 ? r : '·'} / ${F}`;
}

function detailLine(def: CardDef): string {
  const n = binderSim.save.cards[def.id] ?? 0;
  const no = `No. ${String(def.no).padStart(3, '0')}`;
  if (n <= 0) return `${no} · 미보유`;
  return `${no} · ${def.name ?? def.id}${n >= 2 ? ` · ×${n}` : ''}`;
}

// ---------------------------------------------------------------- 키 / 버튼
window.addEventListener('keydown', (e) => {
  if ((e.target as HTMLElement)?.tagName === 'INPUT' || (e.target as HTMLElement)?.tagName === 'TEXTAREA') return;
  switch (e.key) {
    case 'r':
    case 'R':
      if (screen === 'open') newPack(); // 바인더에서 R 은 무시한다 (§2.5)
      break;
    case 'b':
    case 'B':
      toggleBinder();
      break;
    case 'h':
    case 'H':
      panel.toggle();
      break;
    case 'd':
    case 'D':
      diag.toggle();
      panel.setDiagChecked(diag.visible);
      panel.refresh();
      break;
    case ' ':
      e.preventDefault();
      panel.toggleAB();
      break;
  }
});
document.getElementById('btn-reset')!.addEventListener('click', () => {
  if (screen === 'open') newPack();
});
document.getElementById('btn-binder')!.addEventListener('click', toggleBinder);
/**
 * 저장 데이터 초기화. 카드·소지금이 전부 날아가고 되돌릴 수 없으므로 확인을 받는다.
 * 통째로 다시 시작하는 게 목적이라 지우고 새로고침한다 — 부분 갱신보다 확실하다.
 */
document.getElementById('wipe')!.addEventListener('click', () => {
  if (!confirm('저장된 카드와 소지금을 전부 지운다. 되돌릴 수 없다.')) return;
  localStorage.removeItem(SAVE_KEY);
  location.reload();
});
document.getElementById('btn-panel')!.addEventListener('click', () => panel.toggle());
document.getElementById('btn-diag')!.addEventListener('click', () => {
  diag.toggle();
  panel.setDiagChecked(diag.visible);
  panel.refresh();
});
document.getElementById('btn-ab')!.addEventListener('click', () => panel.toggleAB());

// ---- 경제 버튼
walletEl.addEventListener('click', () => {
  // 개봉 화면에서 팩이 잠겨 있으면 닫아도 다음 프레임에 다시 열린다 (그게 맞다 — 다른 할 게 없다)
  shopOpen = !shopOpen;
});
document.getElementById('btn-shop-close')!.addEventListener('click', () => {
  // **닫으면 닫힌 채로 둔다.** 팩이 잠겨 있어도 마찬가지다 — 바인더(B)에서 카드를 팔면
  // 회복되므로 광고를 강제할 이유가 없다. 팩을 다시 만지면 그때 열린다.
  shopOpen = false;
});
adBtn.addEventListener('click', () => doAd(false));
adLongBtn.addEventListener('click', () => doAd(true));
sellBtn.addEventListener('click', () => doSell());

// ---------------------------------------------------------------- 입력 라우팅
// Sim 이 직접 콜백을 잡으면 바인더에서 누른 탭이 개봉 화면 큐에 남는다. 여기서 활성 sim 에만 준다.
// 상점이 떠 있거나 팩이 잠겨 있으면 캔버스 입력을 여기서 버린다 (sim 큐에 안 쌓이게).
// 버튼들은 캔버스 밖의 HTML 이라 이 경로를 안 탄다.
let swallowUp = false;
input.onDown = (p, t) => {
  audio.warmup();
  if (shopOpen) {
    // 바깥을 탭하면 상점을 닫는다. 팩이 잠겨 있어도 닫힌다 — 바인더에서 카드를 팔면 되기 때문이다.
    shopOpen = false;
    swallowUp = true;
    return;
  }
  if (screen === 'open' && packLocked()) {
    // 잠긴 팩을 만지면 상점을 다시 띄운다 (닫은 뒤 팩을 눌렀다면 살 뜻이므로)
    shopOpen = true;
    swallowUp = true;
    return;
  }
  swallowUp = false;
  if (screen === 'open') sim.pointerDown(p, t);
  else {
    if (binderSim.hitTestForTouchTick(p)) audio.touchTick();
    binderSim.pointerDown(p, t);
  }
};
input.onUp = (p, t, v) => {
  if (swallowUp) {
    swallowUp = false;
    return;
  }
  if (screen === 'open') sim.pointerUp(p, t, v);
  else binderSim.pointerUp(p, t, v);
};

sim.onEvent = simEvent;
// **결제 지점을 sim 에 꽂는다.** 팩이 찢어지기 시작하는 첫 프레임에 여기로 온다.
sim.onCutCharge = chargeForCut;
diag.attachBinder(binderSim, () => screen, renderer.binder);

// ---------------------------------------------------------------- 루프
const hMs = 1000 / WORLD.physicsHz;
const h = 1 / WORLD.physicsHz;
let last = performance.now();
let acc = 0;
const interp = new RS();
const binderInterp = new BinderRS();

function frame(now: number): void {
  let dt = now - last;
  last = now;
  if (dt > 100) dt = 100; // 탭 복귀 등: 물리 시간을 현실에 다시 맞춘다
  stepGain(dt / 1000); // 판매 금액 오버레이 (화면과 무관하게 흐른다)
  bgm.tick(); // BGM 스텝 예약 (페이드아웃 중에도 돌아야 끝까지 들린다)

  // 타이틀은 물리가 없다 — 버튼 움직임만 계산하고 렌더도 건너뛴다.
  // (개봉 sim 은 시간이 멈춘 채 대기하므로 시작을 누르면 손 안 댄 팩 그대로다)
  if (screen === 'title') {
    stepTitle(dt / 1000);
    input.endFrame(now);
    requestAnimationFrame(frame);
    return;
  }

  acc += dt;
  let simT = now - acc;
  let steps = 0;
  while (acc >= hMs) {
    simT += hMs;
    // 한 스텝에 sim 은 하나만 돈다. 비활성 sim 은 시간이 멈춘다.
    // input.consume() 은 **어느 화면이든 매 스텝** 부른다 (안 부르면 큐가 쌓이고 속도 추정이 멈춘다).
    if (screen === 'open') {
      sim.snapshot();
      input.consume(simT, h);
      sim.step(h, simT);
    } else {
      binderSim.snapshot();
      input.consume(simT, h);
      binderSim.step(h, simT);
    }
    diag.recordStep();
    acc -= hMs;
    steps++;
    if (steps > 60) {
      acc = 0;
      break;
    }
  }
  const alpha = acc / hMs;
  if (screen === 'open') {
    interp.lerp(sim.prev, sim.rs, alpha);
    renderer.draw(interp, null);
  } else {
    binderInterp.lerp(binderSim.prev, binderSim.rs, alpha);
    renderer.draw(null, binderInterp);
  }
  checkTransitions();
  updateOverlay();
  diag.frame(dt);
  input.endFrame(now);
  requestAnimationFrame(frame);
}
requestAnimationFrame(frame);

// 진동은 사용자 제스처가 한 번이라도 있어야 허용된다. 자가진단에서 이걸 구분해 보여주려고 기록한다.
window.addEventListener('pointerdown', () => (haptics.interacted = true), { once: true, capture: true, passive: true });

// **시작할 때는 아무것도 안 산다.** 팩은 공짜로 테이블에 놓이고, 뜯는 순간에 값이 빠진다.
// openPaid 가 남아 있으면(자르다 새로고침) 그 팩은 이미 값을 치른 것이라 또 안 뺀다 —
// 뽑기 결과는 저장하지 않으므로 카드는 새로 뽑히지만, 그건 이 규칙과 무관하다.

// ?binder=1 / ?face=n — 검증 도구가 바인더로 바로 들어온다
//
// **검증 도구(tools/*.mjs 20여 개)는 전부 개봉 화면을 전제한다.** 타이틀이 앞에 서면
// 한꺼번에 깨지는데, 도구들이 URL 을 각자 정의하고 있어 한 곳만 고쳐서는 안 된다.
// 그래서 **헤드리스 자동화면 자동으로 건너뛴다** (puppeteer 는 navigator.webdriver 가 true).
// `?title=1` 을 붙이면 자동화에서도 타이틀을 볼 수 있다 (타이틀 자체를 검증할 때).
const titleParam = url.searchParams.get('title');
const automated = navigator.webdriver === true;
if (titleParam === '0' || (automated && titleParam !== '1')) screen = 'open';

const faceParam = Number(url.searchParams.get('face'));
if (url.searchParams.get('binder') === '1') {
  screen = 'open'; // 바인더로 바로 들어갈 때도 타이틀은 건너뛴다
  openBinder(Number.isFinite(faceParam) && faceParam >= 1 ? faceParam : undefined);
} else {
  syncOverlay();
}

// 타이틀 BGM 은 여기서 한 번 시도한다. 자동재생 정책에 막히면 첫 입력(bgmGestureHook)에서 다시 시도한다.
ensureTitleBgm();

// 자동 테스트/콘솔 진단용 훅 (tools/*.mjs)
(window as unknown as { __cr: unknown }).__cr = {
  sim,
  binderSim,
  P,
  renderer,
  // 시드를 바꿔 다시 뽑는다 (null = 매번 새로). 뽑기 통계·결정론 확인용.
  setSeed: (s: number | null) => {
    setDrawSeed(s);
    reset();
    return CARD_DRAW.kinds.slice();
  },
  events: () => eventLog.slice(),
  // events() 는 **복사본**이라 도구가 splice 해도 안 지워진다. 비우려면 이걸 부른다.
  clearEvents: () => {
    eventLog.length = 0;
  },
  // 경제. 도구가 소지금을 직접 세팅해 "돈 부족" 상태를 만든다.
  econ: {
    /** 지금 팩의 값을 치른다 (자르기 없이 — 검증 전용). sim 이 부르는 것과 같은 경로다. */
    buy: () => chargeForCut(),
    /** 상세 보기 중인 카드를 한 장 판다. id 를 주면 그 카드를 (상세 보기 없이) 판다. */
    sell: (id?: string) => {
      if (id === undefined) {
        doSell();
        return binderSim.save.money;
      }
      const got = sellOne(binderSim.save, id);
      if (got !== null) renderer.binder.setCounts(binderSim.save.cards);
      return got;
    },
    ad: (long = false) => doAd(long),
    /** 값을 치른 팩을 버린다 (검증 전용 — "돈 부족으로 잠긴" 상태를 만든다). */
    discard: () => {
      discardPack(binderSim.save);
      return binderSim.save.openPaid;
    },
    /** 소지금을 달러로 설정한다 (검증 전용). */
    setMoney: (dollars: number) => {
      const s = binderSim.save;
      s.money = Math.max(0, Math.round(dollars * 100));
      saveBinder(s);
      return s.money;
    },
    shop: (open: boolean) => {
      shopOpen = open;
    },
  },
  binder: {
    open: (face?: number) => openBinder(face),
    close: () => {
      binderSim.requestExit();
    },
    load: (json: unknown) => applyBinderJson(json as object),
    clear: () => {
      clearBinder();
      const s = emptySave();
      binderSim.setSave(s);
      renderer.binder.setCounts(s.cards);
    },
    goto: (f: number) => binderSim.goto(f),
    /** 보이는 면의 칸 중심 화면 px — 도구가 탭 좌표를 얻는다. face 를 주면 그 면(펼침의 좌/우). */
    cellScreenPos: (cell: number, face?: number) => {
      const o = { x: 0, y: 0, w: 0, h: 0 };
      binderSim.cellCenter(cell, o, face ?? binderSim.face);
      return renderer.toScreen(o.x - binderSim.viewX, o.y);
    },
    faceWidth: () => binderSim.W,
    viewX: () => binderSim.viewX,
    detailScreenWidth: () => renderer.binder.detailScreenWidth(binderSim.rs),
  },
  registry: {
    override: (defs: CardDef[]) => {
      const n = overrideRegistry(defs);
      renderer.binder.invalidate();
      return n;
    },
    count: () => CARD_DEFS.length,
    ids: () => CARD_DEFS.map((d) => d.id),
    faceCount: () => faceCount(),
    byId: (id: string) => {
      const d = CARD_BY_ID.get(id);
      return d ? { ...d } : null;
    },
  },
  // 레이어 z. 도구가 "상세 카드가 앞판 위에 있는가" 를 파라미터에서 유도해 판정한다 (BINDER_DESIGN §12).
  layers: { ...BINDER_LAYERS },
  state: () => ({
    screen,
    phase: sim.phase,
    draw: CARD_DRAW.kinds.slice(),
    ranks: CARD_DRAW.ranks.slice(),
    defIds: CARD_DRAW.defIds.slice(),
    // 개봉 화면 8자리에 실제로 붙은 카드 그림. null 이면 그 자리는 아직(또는 영영) 등급 그림이다.
    packArt: renderer.packArtIds(),
    perfScreenY: renderer.toScreen(0, sim.perfY).y,
    stackScreenY: renderer.toScreen(0, P.open.stackY).y,
    cut: { dir: sim.cut.dir, front: +sim.cut.front.toFixed(3), F: +sim.cut.F.toFixed(3), Rk: +sim.cut.Rk.toFixed(3), moving: sim.cut.moving, active: sim.cut.active },
    open: { t: +sim.open.t.toFixed(3), topVis: sim.rs.topVis, bodyY: +sim.rs.bodyY.toFixed(3) },
    flip: { theta: +sim.flip.theta.toFixed(3), roll: +sim.flip.roll.toFixed(3) },
    sw: {
      state: sim.sw.state,
      top: sim.sw.top,
      pos: [+sim.sw.px.toFixed(3), +sim.sw.py.toFixed(3)],
      vel: [+sim.sw.vx.toFixed(3), +sim.sw.vy.toFixed(3)],
      bend: +sim.sw.bend.toFixed(3),
      lagDir: [+sim.sw.bdx.toFixed(3), +sim.sw.bdy.toFixed(3)],
      reveal: +sim.sw.reveal.toFixed(3),
      revealIdx: sim.sw.revealIdx,
      rareT: +sim.sw.rareT.toFixed(3),
      rareActive: sim.sw.rareActive,
      rarePulses: sim.sw.rarePulses,
      slips: sim.sw.slipCount,
      flyers: sim.sw.flyers.length,
      rel: sim.sw.lastReleaseVel,
      rareCutIdx: sim.sw.rareCutIdx,
    },
    // AR·UR 등장 연출. 소리·빛은 헤드리스로 못 보므로 **상태와 셰이더 값**으로 확인한다.
    revealFx: {
      idx: sim.fx.idx,
      rank: sim.fx.rank,
      t: +sim.fx.t.toFixed(3),
      scale: +sim.fx.scale.toFixed(3),
      band: +sim.rs.fxBand.toFixed(3),
      strength: +sim.rs.fxStrength.toFixed(3),
      flash: +sim.rs.fxFlash.toFixed(3),
    },
    binder: {
      mode: binderSim.mode,
      face: binderSim.face,
      faceCount: faceCount(),
      seg: {
        kind: ['none', 'flip', 'pan', 'edge'][binderSim.seg.kind],
        dir: binderSim.seg.dir,
        from: binderSim.seg.from,
        to: binderSim.seg.to,
        sheet: binderSim.seg.sheet,
        u: +binderSim.seg.u.toFixed(4),
        v: +binderSim.seg.v.toFixed(4),
        peeled: binderSim.seg.peeled,
        tau: +binderSim.seg.tau.toFixed(3),
      },
      W: +binderSim.W.toFixed(4),
      H: +binderSim.H.toFixed(4),
      aspect: +binderSim.aspect.toFixed(4),
      spread: binderSim.spread,
      spreadIndex: binderSim.spreadIndex,
      leftFace: binderSim.leftFace,
      rightFace: binderSim.rightFace,
      viewX: +binderSim.viewX.toFixed(4),
      theta: +binderSim.theta.toFixed(4),
      stow: {
        t: +binderSim.stow.t.toFixed(3),
        ct: +binderSim.stow.ct.toFixed(3),
        active: binderSim.stow.active,
        phase: binderSim.stow.phase,
        cur: binderSim.stow.cur,
        n: binderSim.stow.n,
        turned: binderSim.stow.turned,
        landed: [...binderSim.stow.landed],
        // 정렬된 순서의 면·슬롯. "카드가 제 페이지로 넘어가 들어간다" 를 도구가 확인할 수 있어야 한다.
        faces: [...binderSim.stow.face],
        slots: [...binderSim.stow.slot],
        auto: binderSim.seg.auto,
      },
      cache: renderer.binder.cardCache.residentCells(),
      detail: {
        phase: binderSim.det.phase,
        slot: binderSim.det.slot,
        cell: binderSim.det.cell,
        owned: binderSim.det.owned,
        a: { x: +binderSim.det.ax.toFixed(3), y: +binderSim.det.ay.toFixed(3), s: +binderSim.det.as.toFixed(3), z: +binderSim.det.az.toFixed(3) },
        dim: +binderSim.det.dim.toFixed(3),
        texSource: renderer.binder.detailSourceA,
        detailCache: renderer.binder.cardCache.residentDetails(),
      },
    },
    econ: {
      money: binderSim.save.money,
      moneyText: fmtCents(binderSim.save.money),
      packPrice: packPriceCents(),
      openPaid: binderSim.save.openPaid,
      locked: packLocked(),
      canAfford: canAffordPack(binderSim.save),
      deadEnd: isDeadEnd(binderSim.save),
      sellable: sellableValue(binderSim.save),
      shopOpen,
      adPlaying: adLongUntil > 0,
      // 떠오르는 금액 텍스트. 도구가 "뜯는 순간 -$4.49 가 떴는가" 를 여기서 본다.
      float: { key: sim.rs.floatKey, text: sim.float.text, sign: sim.float.sign, alpha: +sim.rs.floatA.toFixed(3), dy: +sim.rs.floatDY.toFixed(3) },
      sellShown: sellBtn.style.display !== 'none',
      sellEnabled: sellBtn.style.display !== 'none' && !sellBtn.disabled,
      sellText: sellBtn.textContent,
    },
    save: { kinds: Object.keys(binderSim.save.cards).length, total: totalOwned(binderSim.save), packs: binderSim.save.packs, lastFace: binderSim.save.lastFace, cards: { ...binderSim.save.cards } },
    input: { vFast: input.vFast, samplesPerSec: input.statSamplesPerSec },
    audio: { ready: audio.ready, latency: audio.latencyMs, rareLevel: +audio.lastRareLevel.toFixed(4), pageLevel: +audio.lastPageLevel.toFixed(4), frictionLevel: +audio.lastFrictionLevel.toFixed(4) },
      bgm: { playing: bgm.playing, step: bgm.debugStep },
    textures: renderer.gl.info.memory.textures,
  }),
};
